// src/pages/ChemicalUsagePage.js
// Menu: "Chemical Usage" - Shows chemical usage events grouped by bay/chemical
import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { api } from '../services/api';
import PageSkeleton from '../components/PageSkeleton';
import { useSettings } from '../hooks/useSettings';
import { isAssignedChemical } from '../utils/chemicalSort';
import { useLocation } from '../contexts/LocationContext';
import { useBayConfig } from '../hooks/useBayConfig';
import { useSystemSettings } from '../context/SystemSettingsContext';
import { formatChipId } from '../utils/bayHelpers';
import { useRecentEvents } from '../hooks/useRecentEvents';
import { useControllerBays } from '../hooks/useControllerBays';
import './ChemicalUsagePage.css';

// Helper function to format timestamp
const formatTimestamp = (timestamp, timezone) => {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: timezone
  }).replace(',', '');
};

// Time range options (replaces count-based "last N uses")
const timeRanges = [
  { value: 6, label: 'Last 6 Hours' },
  { value: 12, label: 'Last 12 Hours' },
  { value: 24, label: 'Last 24 Hours' },
  { value: 48, label: 'Last 48 Hours' },
  { value: 72, label: 'Last 72 Hours' },
  { value: 168, label: 'Last 7 Days' }
];

// Default to 12 hours for initial load.
// Demo users (org_001) get 48 hours default so they can see frozen demo data
const DEFAULT_TIME_RANGE = 12;
const DEMO_TIME_RANGE = 48;

function ChemicalUsagePage() {
  const [events, setEvents] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [expandedInjectors, setExpandedInjectors] = useState(new Set()); // Allow multiple expanded
  const [timeRangeHours, setTimeRangeHours] = useState(null); // Will be set based on org
  const [sortByBox, setSortByBox] = useState(false);
  const [showBoxId, setShowBoxId] = useState(false);
  // Wash-controller reader sites only: injector open time per event ("Valve" column).
  const [valveTimes, setValveTimes] = useState(null); // { bay, events: { [eventId]: {valveMs, flowMs, name} }, missed: [...] }

  const { getColorForValue } = useSettings();
  const {
    selectedLocationId,
    selectedOrgId,
    locationsLoadedForOrgId,
    loading: locationLoading
  } = useLocation();
  const { activeBays, bayDisplayNames, loading: bayConfigLoading } = useBayConfig();
  const { timezone } = useSystemSettings();
  const theme = useTheme();
  const showValve = useControllerBays(selectedLocationId).hasController;
  // Keyed on the time value, not the Date object: rebuilding the list (e.g. the
  // Box ID / sort buttons) must not refetch valve times.
  const lastUpdatedMs = lastUpdated ? lastUpdated.getTime() : null;
  useEffect(() => {
    if (!showValve || !timeRangeHours) { setValveTimes(null); return undefined; }
    let active = true;
    api.getValveTimes(timeRangeHours, selectedLocationId)
      .then(data => { if (active) setValveTimes(data?.available ? data : null); })
      .catch(() => { if (active) setValveTimes(null); });
    return () => { active = false; };
  }, [showValve, timeRangeHours, selectedLocationId, lastUpdatedMs]);
  const locationsPending = Boolean(
    locationLoading ||
    (selectedOrgId && locationsLoadedForOrgId !== selectedOrgId)
  );

  const eventsReady = Boolean(
    selectedOrgId &&
    timeRangeHours !== null &&
    !bayConfigLoading &&
    !locationsPending
  );
  const {
    events: recentEvents,
    error: recentEventsError,
    isLoading: recentEventsLoading,
    lastSuccessfulAt,
    refresh: refreshRecentEvents
  } = useRecentEvents({ hours: timeRangeHours || DEFAULT_TIME_RANGE, enabled: eventsReady });

  // Group events by bay and injector key
  const [groupedEvents, setGroupedEvents] = useState({});
  const processedEventsKey = React.useRef(null);

  // Refs for syncing scrollbars
  const topScrollRef = React.useRef(null);
  const contentScrollRef = React.useRef(null);

  // Set initial time range based on org (demo users get 48 hours to see frozen data)
  useEffect(() => {
    if (selectedOrgId && timeRangeHours === null) {
      const defaultRange = selectedOrgId === 'org_001' ? DEMO_TIME_RANGE : DEFAULT_TIME_RANGE;
      setTimeRangeHours(defaultRange);
    }
  }, [selectedOrgId, timeRangeHours]);

  useEffect(() => {
    setLoading(recentEventsLoading || (eventsReady && !lastSuccessfulAt && !recentEventsError));
    if (recentEventsError) {
      setError('Unable to refresh chemical usage data.');
    } else if (recentEvents.length || lastSuccessfulAt) {
      setError(null);
    }
  }, [recentEventsLoading, recentEvents.length, recentEventsError, lastSuccessfulAt, eventsReady]);

  useEffect(() => {
    let active = true;
    if (!eventsReady) return undefined;
    api.getSensors(selectedLocationId)
      .then(data => { if (active) setSensors(data || []); })
      .catch(err => {
        console.error('Error fetching sensors:', err);
        if (active) setError('Unable to refresh chemical usage data.');
      });
    return () => { active = false; };
  }, [eventsReady, selectedLocationId]);

  // processEvents is intentionally kept local because it closes over the page's
  // display configuration; the event/sensor signature below bounds execution.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (eventsReady && !recentEventsError) {
      // The Box ID and sort buttons change the injector list, so they are part
      // of the key: clicking one rebuilds the list from the data already loaded.
      const processKey = `${lastSuccessfulAt?.getTime() || 'pending'}:${recentEvents.length}:${sensors.length}:${showBoxId}:${sortByBox}`;
      if (processedEventsKey.current === processKey) return;
      processedEventsKey.current = processKey;
      processEvents(recentEvents, sensors, lastSuccessfulAt ? new Date(lastSuccessfulAt) : new Date());
    }
  }, [recentEvents, sensors, eventsReady, recentEventsError, lastSuccessfulAt, showBoxId, sortByBox]);

  // Process events data and update state
  const processEvents = async (eventsData, sensorsData = null, successfulAt = new Date()) => {
    try {
      // Fetch sensors if not provided (when using cache)
      if (!sensorsData) {
        sensorsData = await api.getSensors(selectedLocationId);
      }

      // Store injectors PER BAY - each bay has its own list
      // Use chip+sensor as unique key to avoid display name collisions
      const injectorsByBay = {};
      (sensorsData || []).forEach(sensor => {
        if (sensor.chemical_name && sensor.chemical_name.trim().toLowerCase() !== 'unassigned') {
          const bayKey = sensor.bay;
          if (!bayKey || !activeBays.includes(bayKey)) return;

          if (!injectorsByBay[bayKey]) {
            injectorsByBay[bayKey] = [];
          }

          const chip = sensor.chip;
          const sensorId = sensor.sensor_id ?? sensor.sensorId;
          const injectorKey = `${chip}.${sensorId}`;
          const baseDisplayName = sensor.description
            ? `${sensor.chemical_name} (${sensor.description})`
            : sensor.chemical_name;
          const boxDetails = showBoxId
            ? ` (${formatChipId(chip) || 'unknown'} ${sensorId ?? 'unknown'})`
            : '';
          const displayName = `${baseDisplayName}${boxDetails}`;

          injectorsByBay[bayKey].push({
            key: injectorKey,
            displayName,
            chipDisplay: formatChipId(chip) || '',
            sensorId: sensorId ?? '',
            display_order: sensor.display_order ?? sensor.displayOrder ?? Infinity
          });
        }
      });

      // Sort each bay's injectors by display_order, or optionally by box/sensor.
      Object.keys(injectorsByBay).forEach(bay => {
        injectorsByBay[bay].sort((a, b) => {
          if (sortByBox) {
            const boxCompare = a.chipDisplay.localeCompare(b.chipDisplay, undefined, { numeric: true });
            if (boxCompare !== 0) return boxCompare;

            const sensorCompare = String(a.sensorId).localeCompare(String(b.sensorId), undefined, { numeric: true });
            if (sensorCompare !== 0) return sensorCompare;

            return a.displayName.localeCompare(b.displayName);
          }

          if (a.display_order !== b.display_order) {
            return a.display_order - b.display_order;
          }
          return a.displayName.localeCompare(b.displayName);
        });
      });

      // Store for rendering - this is now per-bay
      window._injectorsByBay = injectorsByBay;

      if (eventsData && Array.isArray(eventsData)) {
        setEvents(eventsData);
        setLastUpdated(successfulAt);

        // Group events by bay and INJECTOR KEY (chip+sensor) - NOT by display name
        const grouped = {};
        eventsData.forEach(event => {
          // Normalize bay key to format: bay1, bay2, etc.
          let bayKey = event.bay || 'unknown';
          if (bayKey !== 'unknown') {
            const bayNum = bayKey.toLowerCase().replace(/[^0-9]/g, '');
            bayKey = bayNum ? `bay${bayNum}` : bayKey;
          }

          // FILTER: Only include events from active bays
          if (!activeBays.includes(bayKey)) {
            return;
          }

          // Skip unassigned events
          if (!isAssignedChemical(event.chemical)) {
            return;
          }

          // Use chip.sensor as unique injector key (dot separator matches Firestore format)
          const injectorKey = `${event.chip}.${event.sensor}`;

          if (!grouped[bayKey]) {
            grouped[bayKey] = {};
          }

          if (!grouped[bayKey][injectorKey]) {
            grouped[bayKey][injectorKey] = [];
          }

          grouped[bayKey][injectorKey].push(event);
        });

        // Sort events by time (newest first)
        Object.keys(grouped).forEach(bay => {
          Object.keys(grouped[bay]).forEach(injKey => {
            grouped[bay][injKey] = grouped[bay][injKey]
              .sort((a, b) => new Date(b.time) - new Date(a.time));
          });
        });

        setGroupedEvents(grouped);
      }
    } catch (err) {
      console.error('Error processing events:', err);
      throw err;
    }
  };

  const handleRefresh = () => {
    refreshRecentEvents().catch(err => {
      console.error('Error refreshing events:', err);
      setError('Unable to refresh chemical usage data.');
    });
  };

  const toggleInjector = (bay, injectorKey) => {
    const key = `${bay}-${injectorKey}`;
    const newExpanded = new Set(expandedInjectors);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedInjectors(newExpanded);
  };

  const expandAll = () => {
    const allKeys = [];
    Object.keys(groupedEvents).forEach(bay => {
      Object.keys(groupedEvents[bay]).forEach(injKey => {
        allKeys.push(`${bay}-${injKey}`);
      });
    });
    setExpandedInjectors(new Set(allKeys));
  };

  const collapseAll = () => {
    setExpandedInjectors(new Set());
  };
  

  // Sync scrollbars
  const handleTopScroll = (e) => {
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  const handleContentScroll = (e) => {
    if (topScrollRef.current) {
      topScrollRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  // Generate styles from theme
  const styles = getStyles(theme);

  if ((loading || bayConfigLoading) && !events.length) {
    return (
      <div style={styles.container} className="usage-container">
        <PageSkeleton variant="table" />
      </div>
    );
  }

  if (error && !lastUpdated) {
    return (
      <div style={styles.container} className="usage-container">
        <div style={styles.error} role="alert">
          {error}
          <button onClick={handleRefresh} style={styles.retryButton}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const hasAnyGroupedData = Object.keys(groupedEvents).some(bay =>
    Object.keys(groupedEvents[bay]).some(chem => groupedEvents[bay][chem]?.length > 0)
  );

  return (
    <div style={styles.container} className="usage-container">
      <div style={styles.controls} className="usage-controls">
        <div style={styles.leftControls} className="usage-left-controls">
          <div style={styles.expandControls} className="usage-expand-controls">
            <button onClick={expandAll} style={styles.controlButton} className="usage-control-button">
              Expand All
            </button>
            <button onClick={collapseAll} style={styles.controlButton} className="usage-control-button">
              Collapse All
            </button>
            <button
              onClick={() => setShowBoxId(prev => !prev)}
              style={{
                ...styles.controlButton,
                ...(showBoxId ? styles.activeControlButton : {})
              }}
              className="usage-control-button"
            >
              {showBoxId ? 'Hide Box ID' : 'Show Box ID'}
            </button>
            <button
              onClick={() => setSortByBox(prev => !prev)}
              style={{
                ...styles.controlButton,
                ...(sortByBox ? styles.activeControlButton : {})
              }}
              className="usage-control-button"
            >
              {sortByBox ? 'Sort by Display Order' : 'Sort by Box'}
            </button>
          </div>
          
          <div style={styles.usesSelector}>
            <label style={styles.label}>Time Range:</label>
            <select
              value={timeRangeHours}
              onChange={(e) => setTimeRangeHours(Number(e.target.value))}
              style={styles.select} className="usage-select"
            >
              {timeRanges.map(range => (
                <option key={range.value} value={range.value}>{range.label}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div style={styles.rightControls} className="usage-right-controls">
          <span style={styles.updateTime} className="usage-update-time">
            Last successfully updated: {lastUpdated ? formatTimestamp(lastUpdated, timezone) : 'Never'}
          </span>
          <button onClick={handleRefresh} style={styles.refreshButton} className="usage-refresh-button" disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && lastUpdated && (
        <div style={styles.refreshWarning} role="alert">
          <strong>{error}</strong> Showing the last successfully loaded data from {formatTimestamp(lastUpdated, timezone)}.
          <button onClick={handleRefresh} style={styles.retryButton} disabled={loading}>
            Retry
          </button>
        </div>
      )}

      {/* No data warning banner */}
      {!loading && !error && !hasAnyGroupedData && (
        <div style={styles.noDataBanner}>
          <div style={styles.noDataIcon}>📊</div>
          <div style={styles.noDataText}>
            <strong>No chemical usage data found</strong>
            <p>No usage events recorded in the last {timeRangeHours} hours. Try extending the time range above to see older data.</p>
          </div>
        </div>
      )}

      {/* Top scrollbar for easier navigation */}
      <div
        ref={topScrollRef}
        onScroll={handleTopScroll}
        style={styles.topScrollbar}
      >
        <div style={{ width: `${activeBays.length * 400}px`, height: '1px' }}></div>
      </div>

      {/* Scrollable container for bay headers and rows */}
      <div
        ref={contentScrollRef}
        onScroll={handleContentScroll}
        style={styles.scrollContainer} className="usage-scroll-container"
      >
        {/* Bay headers */}
        <div style={{
          ...styles.bayHeaders,
          gridTemplateColumns: `repeat(${activeBays.length}, minmax(400px, 1fr))`
        }}>
          {activeBays.map(bayId => (
            <div key={bayId} style={styles.bayColumn}>
              <h2 style={styles.bayTitle}>
                {bayDisplayNames[bayId] || bayId.replace('bay', 'Bay ')}
              </h2>
            </div>
          ))}
        </div>

        {/* Injector rows - each bay shows its own injectors independently */}
        <div style={styles.chemicalRows}>
          {/* Get max injectors across all bays to determine row count */}
          {(() => {
            const injectorsByBay = window._injectorsByBay || {};
            const maxRows = Math.max(...activeBays.map(bay => (injectorsByBay[bay] || []).length), 0);
            const rows = [];

            for (let rowIdx = 0; rowIdx < maxRows; rowIdx++) {
              rows.push(
                <div key={rowIdx} style={{
                  ...styles.chemicalRow,
                  gridTemplateColumns: `repeat(${activeBays.length}, minmax(400px, 1fr))`
                }}>
                  {activeBays.map(bayId => {
                    const bayInjectors = injectorsByBay[bayId] || [];
                    const injector = bayInjectors[rowIdx];
                    if (!injector) {
                      return <div key={bayId} style={styles.bayColumn}></div>;
                    }
                    return renderBayCell(bayId, injector.key, injector.displayName);
                  })}
                </div>
              );
            }
            return rows;
          })()}
        </div>
      </div>
    </div>
  );

  // Bays the Valve column applies to (a reader site: its one bay; demo sites: every bay).
  function valveBay(bay) {
    if (!valveTimes) return false;
    return Array.isArray(valveTimes.bays) ? valveTimes.bays.includes(bay) : valveTimes.bay === bay;
  }

  // Valve column: controller injector open time for this flow event.
  // Green within 25% of the measured duration; red when more than 25% off, or when
  // chemical flowed with no injector open. "—" = no controller data for it.
  function renderValveCell(event) {
    const v = valveTimes?.events?.[event.eventId];
    if (!v) {
      return <span style={{ ...styles.metricValue, color: theme.palette.grey[500] }}>—</span>;
    }
    if (v.valveMs == null) {
      return <span style={{ ...styles.metricValue, color: theme.palette.error.main }} title="Chemical flowed with no injector open">none</span>;
    }
    const valveS = v.valveMs / 1000;
    // One opening recorded as several dispenses (the flow paused): compare with their whole span.
    const split = v.parts > 1 && v.flowMs > 0;
    const flowS = split ? v.flowMs / 1000 : event.duration;
    const off = flowS > 0 ? Math.abs(valveS - flowS) / flowS : 1;
    const measured = split
      ? `${flowS.toFixed(1)}s measured over ${v.parts} dispenses (the flow paused)`
      : `${flowS.toFixed(1)}s measured`;
    return (
      <span
        style={{ ...styles.metricValue, fontWeight: 600, color: off > 0.25 ? theme.palette.error.main : theme.palette.success.main }}
        title={`${v.name || 'Injector'} open ${valveS.toFixed(1)}s vs ${measured} (${Math.round(off * 100)}% different)`}
      >
        {valveS.toFixed(1)}s
      </span>
    );
  }

  // Helper function to render each bay's cell
  // injectorKey is chip.sensor format, displayName is "Chemical (Description)"
  function renderBayCell(bay, injectorKey, displayName) {
    // Missed dispenses (valve opened, this chemical did not flow) join the card as red rows.
    const missedRows = valveBay(bay)
      ? (valveTimes.missed || [])
        .filter(m => `${m.chip}.${m.sensor}` === injectorKey)
        .map(m => ({ missed: true, time: new Date(m.t).toISOString(), valveMs: m.valveMs, name: m.name }))
      : [];
    if ((!groupedEvents[bay] || !groupedEvents[bay][injectorKey]) && missedRows.length === 0) {
      return (
        <div key={bay} style={styles.bayColumn}>
          <div style={styles.chemicalCard}>
            <div style={styles.chemicalHeader}>
              <span style={styles.expandIcon}>○</span>
              <span style={styles.chemicalName}>{displayName}</span>
              <span style={styles.eventCount}>(0)</span>
            </div>
            <div style={styles.noUsageMessage}>
              No usage in this time range
            </div>
          </div>
        </div>
      );
    }

    // Valve bays show and sort every row by its start (flow start / valve opening): starts line
    // up with the controller, ends wander with sticky valves. Other bays keep the event time (flow end).
    const startTime = e => (e.missed || !valveBay(bay)) ? e.time
      : new Date(new Date(e.time).getTime() - (Number(e.duration) || 0) * 1000).toISOString();
    const chemEvents = [...((groupedEvents[bay] && groupedEvents[bay][injectorKey]) || []), ...missedRows]
      .sort((a, b) => new Date(startTime(b)) - new Date(startTime(a)));
    const isExpanded = expandedInjectors.has(`${bay}-${injectorKey}`);
    const eventsToShow = isExpanded ? chemEvents : chemEvents.slice(0, 2);
    return (
      <div key={bay} style={styles.bayColumn}>
        <div style={styles.chemicalCard}>
                      <div
                        style={styles.chemicalHeader}
                        onClick={() => toggleInjector(bay, injectorKey)}
                      >
                        <span style={styles.expandIcon}>
                          {isExpanded ? '▼' : '▶'}
                        </span>
                        <span style={styles.chemicalName}>{displayName}</span>
                        <span style={styles.eventCount}>
                          ({chemEvents.length})
                        </span>
                      </div>
                      
                      {/* Column headers */}
                      {chemEvents.length > 0 && (
                        <div style={valveBay(bay) ? { ...styles.columnHeaders, gridTemplateColumns: '90px repeat(7, 1fr)' } : styles.columnHeaders}>
                          <span style={styles.headerCell} title={valveBay(bay) ? 'When the flow started (missed rows: when the valve opened)' : undefined}>{valveBay(bay) ? 'Start' : 'Time'}</span>
                          <span style={styles.headerCell}>Volume</span>
                          <span style={styles.headerCell}>Flow</span>
                          <span style={styles.headerCell}>Duration</span>
                          {valveBay(bay) && (
                            <span style={styles.headerCell} title="How long the wash controller held this chemical's injector open. Green = within 25% of the measured duration, red = more than 25% different.">Valve</span>
                          )}
                          <span style={styles.headerCell}>RampUp</span>
                          <span style={styles.headerCell}>RampDn</span>
                          <span style={styles.headerCell}>Between</span>
                        </div>
                      )}
                      
                      {/* Compact event list */}
                      <div style={styles.eventsList}>
                        {eventsToShow.length === 0 ? (
                          <div style={styles.noData}>No uses</div>
                        ) : (
                          eventsToShow.map((event, idx) => event.missed ? (
                            <div key={`missed-${idx}`} style={{ ...styles.compactEvent, gridTemplateColumns: '90px 1fr' }}
                              title={`${event.name || 'Injector'} opened for ${(event.valveMs / 1000).toFixed(1)}s and this chemical did not flow`}>
                              <span style={styles.eventTime}>{formatTimestamp(event.time, timezone)}</span>
                              <span style={{ ...styles.metricValue, color: theme.palette.error.main, fontWeight: 600, textAlign: 'left' }}>
                                valve {(event.valveMs / 1000).toFixed(1)}s · no flow
                              </span>
                            </div>
                          ) : (
                            <div key={idx} style={valveBay(bay) ? { ...styles.compactEvent, gridTemplateColumns: '90px repeat(7, 1fr)' } : styles.compactEvent}>
                              <span style={styles.eventTime} title={valveBay(bay) ? 'Flow start' : undefined}>{formatTimestamp(startTime(event), timezone)}</span>
                              <span style={{
                                ...styles.metricValue,
                                color: getColorForValue(bay, injectorKey, 'volume', event.volume)
                              }}>
                                {event.volume.toFixed(1)}mL
                              </span>
                              <span style={{
                                ...styles.metricValue,
                                color: getColorForValue(bay, injectorKey, 'flow', event.flow_rate)
                              }}>
                                {event.flow_rate.toFixed(1)}mL/m
                              </span>
                              <span style={{
                                ...styles.metricValue,
                                color: getColorForValue(bay, injectorKey, 'duration', event.duration)
                              }}>
                                {event.duration.toFixed(1)}s
                              </span>
                              {valveBay(bay) && renderValveCell(event)}
                              <span style={{
                                ...styles.metricValue,
                                color: getColorForValue(bay, injectorKey, 'rampUp', event.ramp_up_usage)
                              }}>
                                {event.ramp_up_usage.toFixed(1)}s
                              </span>
                              <span style={{
                                ...styles.metricValue,
                                color: getColorForValue(bay, injectorKey, 'rampDown', event.ramp_down_usage)
                              }}>
                                {event.ramp_down_usage.toFixed(1)}s
                              </span>
                              <span style={{
                                ...styles.metricValue,
                                color: getColorForValue(bay, injectorKey, 'between', event.between_usage)
                              }}>
                                {event.between_usage.toFixed(2)}mL
                              </span>
                            </div>
                          ))
                        )}
                      </div>
                      
                      {!isExpanded && chemEvents.length > 2 && (
                        <div style={styles.expandHint}>
                          +{chemEvents.length - 2} more
                        </div>
                      )}
                      
                    </div>
                  </div>
    );
  }
}

// Dynamic styles using theme - enables white-label color customization
const getStyles = (theme) => ({
  container: {
    padding: '20px',
    fontFamily: theme.typography.fontFamily,
    maxWidth: '1400px',
    margin: '0 auto'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    borderBottom: `2px solid ${theme.palette.divider}`,
    paddingBottom: '15px'
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: theme.palette.text.primary,
    margin: 0
  },
  headerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px'
  },
  updateTime: {
    color: theme.palette.text.secondary,
    fontSize: '14px'
  },
  refreshButton: {
    padding: '6px 12px',
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 'bold'
  },
  controls: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    padding: '15px',
    backgroundColor: theme.palette.grey[100],
    borderRadius: '6px'
  },
  leftControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px'
  },
  rightControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  },
  usesSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  bayFilter: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  expandControls: {
    display: 'flex',
    gap: '8px'
  },
  controlButton: {
    padding: '6px 12px',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 'bold',
    transition: 'background-color 0.2s'
  },
  activeControlButton: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText
  },
  label: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: theme.palette.text.secondary
  },
  select: {
    padding: '6px 12px',
    fontSize: '14px',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: '4px',
    backgroundColor: theme.palette.background.paper
  },
  hint: {
    fontSize: '14px',
    color: theme.palette.text.secondary,
    fontStyle: 'italic'
  },
  topScrollbar: {
    overflowX: 'auto',
    overflowY: 'hidden',
    width: '100%',
    height: '20px',
    marginBottom: '10px',
    backgroundColor: theme.palette.grey[100],
    borderRadius: '4px',
    position: 'sticky',
    top: '0',
    zIndex: 100,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  scrollContainer: {
    overflowX: 'auto',
    overflowY: 'visible',
    width: '100%',
    paddingBottom: '10px'
  },
  retryButton: {
    marginLeft: '15px',
    padding: '5px 10px',
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  loading: {
    textAlign: 'center',
    padding: '50px',
    fontSize: '18px',
    color: theme.palette.text.secondary
  },
  error: {
    textAlign: 'center',
    padding: '50px',
    fontSize: '18px',
    color: theme.palette.error.main
  },
  refreshWarning: {
    marginBottom: '16px',
    padding: '12px 16px',
    border: `1px solid ${theme.palette.warning.main}`,
    borderRadius: '6px',
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText
  },
  bayGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '30px'
  },
  baySection: {
    backgroundColor: theme.palette.grey[50],
    borderRadius: '8px',
    padding: '20px',
    border: `1px solid ${theme.palette.divider}`
  },
  bayTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: theme.palette.text.primary,
    marginBottom: '15px',
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    paddingBottom: '10px'
  },
  chemicalGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(650px, 1fr))',
    gap: '15px'
  },
  chemicalCard: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: '6px',
    border: `1px solid ${theme.palette.grey[300]}`,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    width: '100%'
  },
  chemicalHeader: {
    padding: '6px 10px',
    backgroundColor: theme.palette.grey[50],
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    transition: 'background-color 0.2s',
    fontSize: '13px',
    flexWrap: 'wrap',
    minHeight: '36px',
    width: '100%',
    boxSizing: 'border-box'
  },
  chemicalHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  expandIcon: {
    fontSize: '12px',
    color: theme.palette.text.secondary,
    width: '15px'
  },
  chemicalName: {
    fontSize: '13px',
    fontWeight: 'bold',
    color: theme.palette.grey[700],
    marginRight: '5px',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    flex: '1 1 auto',
    minWidth: '0',
    maxWidth: '280px'
  },
  eventCount: {
    fontSize: '12px',
    color: theme.palette.grey[400]
  },
  tableSection: {
    padding: '0'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px'
  },
  th: {
    textAlign: 'left',
    padding: '8px 10px',
    borderBottom: `2px solid ${theme.palette.grey[300]}`,
    backgroundColor: theme.palette.grey[100],
    fontWeight: 'bold',
    color: theme.palette.grey[600],
    fontSize: '12px'
  },
  td: {
    padding: '6px 10px',
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    fontSize: '12px'
  },
  evenRow: {
    backgroundColor: theme.palette.grey[50]
  },
  noDataCell: {
    textAlign: 'center',
    padding: '20px',
    color: theme.palette.grey[400],
    fontStyle: 'italic'
  },
  expandHint: {
    padding: '4px 10px',
    textAlign: 'center',
    backgroundColor: theme.palette.grey[50],
    borderTop: `1px solid ${theme.palette.divider}`,
    fontSize: '12px',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    cursor: 'pointer'
  },
  noData: {
    textAlign: 'center',
    padding: '50px',
    color: theme.palette.grey[400],
    fontSize: '16px',
    fontStyle: 'italic'
  },
  bayHeaders: {
    display: 'grid',
    gap: '10px',
    marginBottom: '10px',
    borderBottom: `2px solid ${theme.palette.grey[900]}`,
    minWidth: 'fit-content'
  },
  chemicalRows: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  chemicalRow: {
    display: 'grid',
    gridAutoRows: 'auto',
    gap: '10px',
    minWidth: 'fit-content'
  },
  chemicalLabel: {
    fontWeight: 'bold',
    padding: '10px',
    display: 'flex',
    alignItems: 'center'
  },
  bayColumn: {
    padding: '4px',
    minWidth: '400px',
    height: 'auto'
  },
  emptyCell: {
    padding: '10px',
    backgroundColor: theme.palette.grey[50],
    borderRadius: '6px',
    border: `1px solid ${theme.palette.grey[300]}`,
    minHeight: '60px',
    overflow: 'hidden',
    wordWrap: 'break-word',
    overflowWrap: 'break-word'
  },
  noUsageMessage: {
    padding: '12px 10px',
    textAlign: 'center',
    color: theme.palette.grey[400],
    fontSize: '12px',
    fontStyle: 'italic',
    backgroundColor: theme.palette.grey[50]
  },
  compactEvent: {
    display: 'grid',
    gridTemplateColumns: '90px repeat(6, 1fr)',
    gap: '4px',
    padding: '0.3rem 0.15rem',
    borderBottom: `1px solid ${theme.palette.grey[100]}`,
    fontSize: '0.65rem',
    alignItems: 'center'
  },
  eventsList: {
    padding: '4px'
  },
  eventTime: {
    fontSize: '0.65rem',
    color: theme.palette.text.secondary
  },
  metricValue: {
    fontSize: '0.65rem',
    fontWeight: '500',
    textAlign: 'center'
  },
  columnHeaders: {
    display: 'grid',
    gridTemplateColumns: '90px repeat(6, 1fr)',
    gap: '4px',
    padding: '0.35rem 0.2rem',
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: theme.palette.grey[100],
    fontSize: '0.65rem',
    fontWeight: 'bold'
  },
  headerCell: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: '0.65rem'
  },
  noDataBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '20px',
    marginBottom: '20px',
    backgroundColor: theme.palette.warning.light,
    border: `2px solid ${theme.palette.warning.main}`,
    borderRadius: '8px'
  },
  noDataIcon: {
    fontSize: '32px'
  },
  noDataText: {
    flex: 1,
    color: theme.palette.warning.dark,
    '& strong': {
      display: 'block',
      marginBottom: '4px',
      fontSize: '16px'
    },
    '& p': {
      margin: 0,
      fontSize: '14px'
    }
  }
});

export default ChemicalUsagePage;
